import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { MatDialogRef } from "@angular/material";
import { FileUploader, FileItem, ParsedResponseHeaders, FileLikeObject, FileUploaderOptions } from 'ng2-file-upload';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';

import { BaseAddressInterceptor } from '../../core/interceptors/BaseAddressInterceptor';
import { CurrentUserService } from '../../security/services/CurrentUserService';
import * as fromStore from '../../security/store/reducers';
import { GetCurrentUserSubmittingEntitiesAction } from '../../security/store/actions';
import { LookupModel } from '../../common/models/LookupModel';
import { FormBuilder, FormGroup } from '@angular/forms';
import { AlertsComponent } from '../../pillar/alerts/components/alerts.component';
import { AppConfigurationService } from '../../core/services/AppConfigurationService';
import { UserService } from '../../customer/services/UserService';
import { JobsService } from '../services/JobsService';

const URL = '/api/SubmissionModule/Job/UploadFiles';
const portalJobSource = "Portal";

@Component({
  selector: 'app-upload-dialog',
  templateUrl: './upload-dialog.component.html',
  styleUrls: ['./upload-dialog.component.scss']
})
export class UploadDialogComponent implements OnInit, OnDestroy {
  public uploader: FileUploader;
  submittingEntitiesLoaded: boolean;
  submittingEntities: LookupModel[];
  destroyed$: Subject<boolean> = new Subject<boolean>();
  hasBaseDropZoneOver: boolean;
  selectedSubmittingEntity: LookupModel;
  fileSizeMB: number = 20;
  uploaded: boolean;

  @ViewChild(AlertsComponent)
  alerts: AlertsComponent;

  constructor(private store: Store<any>,
    public dialogRef: MatDialogRef<UploadDialogComponent>,
    private formBuilder: FormBuilder,
    private baseAddressInterceptor: BaseAddressInterceptor,
    private curentUserService: CurrentUserService,
    private userService: UserService,
    private appConfigurationService: AppConfigurationService,
    private _jobService: JobsService) {
    this.fileSizeMB = appConfigurationService.configuration.maxFileSize;
    this.dialogRef.disableClose = true;
  }

  ngOnInit() {
    this.userService.getCurrentUserSubmittingEntities()
      .subscribe(result => {
        this.submittingEntities = result.model;
        if (this.submittingEntities.length === 1) {
          this.selectSubmittingEntity(this.submittingEntities[0]);
        }
        this.submittingEntitiesLoaded = true;
      });
  }

  fileNameLengthFilter(item?: FileLikeObject, options?: FileUploaderOptions): boolean {
    return item.name.length <= 100;
  }

  selectSubmittingEntity(submittingEntity: LookupModel) {
    this.selectedSubmittingEntity = submittingEntity;
    this.uploader = new FileUploader({
      // url: this.baseAddressInterceptor.getApiUrl(URL + '/' + submittingEntity.key),
      // headers: [{ name: 'Authorization', value: 'Bearer ' + this.curentUserService.userContext.authToken },
      // { name: 'JobSource', value: portalJobSource }
      // ],
      maxFileSize: this.fileSizeMB * 1024 * 1024,
      filters: [
        {
          name: 'fileNameLength',
          fn: this.fileNameLengthFilter
        }],
      disableMultipart: true
    });

    this.uploader.onErrorItem = (item: FileItem, response: string, status: number, headers: ParsedResponseHeaders) => {
      var parseJson = this.tryParseJson(response);
      var message = parseJson !== false ? this.generatErrorMessage(parseJson) : response;
      this.alerts.addError(`Failed uploading '${item.file.name}': ${message}`);
    }

    this.uploader.onAfterAddingFile = (item: FileItem) => {
      this.uploader.queue = this.appConfigurationService.configuration.deletionSequenceEnabled ? this.getSortedSubmissionQuery() : this.uploader.queue.filter(i => (!i.isUploaded && !i.isUploading));
      item.isReady = false;
      this._jobService.getUploadUrl(this.selectedSubmittingEntity.key, item.file.name, item.file.type)
        .subscribe(url => {
          if (url && url.hasError !== true) {
      this.uploaded = false;
            item.isReady = true;
            item.url = url.model;
            item.headers = [];
            item.method = 'PUT'
          } else {
            item.isError = true;
            var errorMessage = `Failed to get upload URL for '${this.truncateWithEllipses(item.file.name, 50)}'`;
            if (url != null && url.messages != null){
              url.messages.forEach(function (message) {
                errorMessage += `. ${message.value}`;
              }); 
            }
            item.onError(errorMessage, 500, null);
            this.alerts.addError(errorMessage);
          }
        });
    }

    this.uploader.onWhenAddingFileFailed = (item: FileLikeObject, filter: any, options: any) => {
      var error = `Cannot add '${this.truncateWithEllipses(item.name, 50)}': `;
      if (filter.name === 'fileSize') {
        error += `File too large must be less than or equal to ${this.fileSizeMB} MB.`;
      } else if (filter.name === 'fileNameLength') {
        error += `File name too long must be less than or equal to 100.`;
      }
      this.alerts.addError(error);
    }

    this.uploader.onCompleteItem = (item: FileItem) => {
      this._jobService.fileUploaded(this.selectedSubmittingEntity.key, item.url)
        .subscribe(response => {
          if (!response.isSuccessStatusCode){
            var message = response.reasonPhrase;
            this.alerts.addError(`Failed uploading '${item.file.name}': ${message}`);
          }     
        });
    }
  }

  private truncateWithEllipses(text, max) {
    return text.substr(0, max - 1) + (text.length > max ? '...' : '');
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
  }

  public fileOverBase(e: any): void {
    this.hasBaseDropZoneOver = e;
  }

  close() {
    this.dialogRef.close();
  }

  private tryParseJson(jsonString) {
    try {
      var o = JSON.parse(jsonString);
      if (o && typeof o === "object") {
        return o;
      }
    } catch (e) {
    }
    return false;
  }

  private generatErrorMessage(s: any) {
    var reasonPhrase = s.reasonPhrase == null ? "" : s.reasonPhrase;
    var message = s.message == null ? "" : s.message;
    var exceptionMessage = s.exceptionMessage == null ? "" : s.exceptionMessage;
    var fullMessage = reasonPhrase + " " + message + " " + exceptionMessage;
    return fullMessage;
  }

  private deleteAll() {
    this.uploader.queue = [];
  }

  private removeFile(item: FileItem) {
    var index = this.uploader.queue.indexOf(item, 0);
    if (index > -1) {
      this.uploader.queue.splice(index, 1);
    }
  }

  private uploadAllFiles() {
    this.uploader.uploadAll();
    this.uploaded = true;
  }

  private isReady() {
    return this.uploader.queue.every(i => i.isReady);
  }

  private dropped(e: any): void {
    this.uploaded = false;
  }

  getSortedSubmissionQuery() {
    var ordering = {};
    var sortOrder = this.appConfigurationService.configuration.deletionSequence.split(",").map(Function.prototype.call, String.prototype.trim);
    for (var i = 0; i < sortOrder.length; i++) {
      ordering[sortOrder[i]] = i;
    }
    var deleteQueue = this.uploader.queue.filter(i => (!i.isUploaded && !i.isUploading && i.file.name.indexOf("DataSet_Delete") !== -1)).sort(
      (a, b) => (ordering[this.getDataSet(a.file.name)] - ordering[this.getDataSet(b.file.name)]) || a.file.name.localeCompare(this.getDataSet(b.file.name)));
    var undoDeleteQueue = this.uploader.queue.filter(i => (!i.isUploaded && !i.isUploading && i.file.name.indexOf("DataSet_UndoDelete") !== -1)).sort(
      (a, b) => (ordering[this.getDataSet(a.file.name)] - ordering[this.getDataSet(b.file.name)]) || a.file.name.localeCompare(this.getDataSet(b.file.name))).reverse();
    var OtherQueue = this.uploader.queue.filter((i) => !i.isUploaded && !i.isUploading && !deleteQueue.includes(i) && !undoDeleteQueue.includes(i)).sort(
      (a, b) => (ordering[this.getDataSet(a.file.name)] - ordering[this.getDataSet(b.file.name)]) || a.file.name.localeCompare(this.getDataSet(b.file.name))).reverse();
    return OtherQueue.concat(deleteQueue).concat(undoDeleteQueue);
  }

  getDataSet(name: string) {
    var dataSet = name.substr(0, name.lastIndexOf('DataSet'));
    if (dataSet.lastIndexOf('Version') !== -1) {
      dataSet = dataSet.substr(0, dataSet.lastIndexOf('Version'));
    }
    return dataSet;
  }
}



// WEBPACK FOOTER //
// ./src/app/submission/components/upload-dialog.component.ts